/**
 * SocialCapitalNav — the dedicated top navigation for socialcapital.cc.
 *
 * Renders 7 primary tabs (Home / About / Earn / How It Works / Impact /
 * Community / My Impact Passport) with hover-and-click dropdown menus,
 * plus the right-side utility bar (Search · Language · Sign In · Start
 * Contributing). Auth-aware: once signed in the "Sign In" button flips
 * to an avatar + account menu.
 *
 * Behaviour
 * ---------
 * • Dropdown opens on hover (desktop) and click (touch); one open at a
 *   time; closes on outside click, ESC, or route change.
 * • Every menu item preserves the current ?site query via
 *   `withBrandSearch` so previewing SocialCapital from the shared host
 *   never drops the brand.
 * • Mobile: primary tabs collapse into a hamburger sheet; "Start
 *   Contributing" stays visible as the sticky primary CTA.
 *
 * The menu tree lives in `NAV_TREE` below — one source of truth so
 * links here match the route stubs registered in App.js.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Search, Globe2, Bell, ChevronDown, Menu, X, Sparkles,
  UserCircle, LogIn, LayoutDashboard,
} from "lucide-react";
import { withBrandSearch } from "@/hooks/useSiteSlug";
import { useAuth } from "@/contexts/AuthContext";

/* ------------------------------------------------------------------ */
/*  Menu tree — the single source of truth                            */
/* ------------------------------------------------------------------ */

export const NAV_TREE = [
  {
    key: "home", label: "Home", href: "/",
    quick: [
      { label: "What is Social Capital?", href: "/social-capital" },
      { label: "Ways to earn",            href: "/earn" },
      { label: "View global impact",      href: "/impact" },
      { label: "Explore badges",          href: "/badges" },
      { label: "Read success stories",    href: "/impact/stories" },
      { label: "Join SocialCapital",      href: "/register" },
      { label: "Sign in to My Passport",  href: "/passport/me" },
    ],
  },
  {
    key: "about", label: "About", href: "/social-capital",
    groups: [{
      title: "The Story",
      items: [
        { label: "What Is Social Capital?", href: "/social-capital" },
        { label: "Why We Built It",         href: "/why-we-build" },
        { label: "Mission, Vision & Values", href: "/vision-mission" },
        { label: "SocialPulse Ecosystem",    href: "/ecosystem" },
        { label: "Who Can Participate?",     href: "/who-can-participate" },
        { label: "Partners & Supporters",    href: "/partners" },
        { label: "Frequently Asked Questions", href: "/faqs" },
        { label: "Contact Us",               href: "/contact" },
      ],
    }],
  },
  {
    key: "earn", label: "Earn", href: "/earn",
    cta: { label: "Submit an Action", href: "/earn/submit" },
    groups: [
      {
        title: "By Category",
        items: [
          { label: "Help People",              href: "/earn/help-people" },
          { label: "Volunteer",                href: "/earn/volunteer" },
          { label: "Share Knowledge",          href: "/earn/share-knowledge" },
          { label: "Mentor Someone",           href: "/earn/mentor" },
          { label: "Protect Nature",           href: "/earn/protect-nature" },
          { label: "Rescue Food",              href: "/earn/rescue-food" },
          { label: "Support Farmers",          href: "/earn/support-farmers" },
          { label: "Donate & Reuse",           href: "/earn/reuse-donate" },
          { label: "Organize Events",          href: "/earn/organize-events" },
          { label: "Strengthen Communities",   href: "/earn/strengthen-communities" },
          { label: "Support Education",        href: "/earn/support-education" },
          { label: "Document Heritage",        href: "/earn/heritage" },
          { label: "Create Memorials",         href: "/earn/memorials" },
          { label: "Responsible Business",     href: "/earn/responsible-business" },
        ],
      },
      {
        title: "Get Started",
        items: [
          { label: "All Earning Activities", href: "/earn/all" },
          { label: "Activities Near Me",     href: "/earn/near-me" },
          { label: "Submit an Action",       href: "/earn/submit", highlight: true },
        ],
      },
    ],
  },
  {
    key: "how", label: "How It Works", href: "/how-it-works",
    groups: [
      {
        title: "The Scoring System",
        items: [
          { label: "Getting Started",           href: "/how-it-works/getting-started" },
          { label: "Social Capital Formula",    href: "/how-it-works/formula" },
          { label: "Point Values & Categories", href: "/how-it-works/point-values" },
          { label: "Verification Levels",       href: "/how-it-works/verification" },
          { label: "Trust & Reputation",        href: "/how-it-works/trust" },
          { label: "Quality & Impact Multipliers", href: "/how-it-works/multipliers" },
        ],
      },
      {
        title: "Progression & Trust",
        items: [
          { label: "Badges & Achievements", href: "/how-it-works/badges" },
          { label: "Contribution Levels",   href: "/how-it-works/levels" },
          { label: "Point Expiration Policy", href: "/how-it-works/expiration" },
          { label: "Fraud & Abuse Prevention", href: "/how-it-works/fraud" },
          { label: "Corrections & Appeals",  href: "/how-it-works/appeals" },
          { label: "Governance & Transparency", href: "/how-it-works/governance" },
        ],
      },
    ],
  },
  {
    key: "impact", label: "Impact", href: "/impact",
    groups: [
      {
        title: "See the Numbers",
        items: [
          { label: "Global Impact Dashboard", href: "/impact/dashboard" },
          { label: "Live Impact Map",         href: "/impact/map" },
          { label: "Impact by Country",       href: "/impact/country" },
          { label: "Impact by State/Province", href: "/impact/state" },
          { label: "Impact by City/Village",  href: "/impact/city" },
          { label: "Impact by Category",      href: "/impact/category" },
        ],
      },
      {
        title: "Projects & Stories",
        items: [
          { label: "Community Projects",     href: "/impact/projects" },
          { label: "Campaigns & Challenges", href: "/impact/campaigns" },
          { label: "Success Stories",        href: "/impact/stories" },
          { label: "Member Spotlights",      href: "/impact/member-spotlights" },
          { label: "Organization Spotlights", href: "/impact/org-spotlights" },
          { label: "Annual Impact Report",   href: "/impact/annual-report" },
        ],
      },
    ],
  },
  {
    key: "community", label: "Community", href: "/community",
    groups: [
      {
        title: "People & Organizations",
        items: [
          { label: "Find Members",         href: "/community/members" },
          { label: "Local Communities",    href: "/community/local" },
          { label: "Organizations",        href: "/community/organizations" },
          { label: "Schools & Universities", href: "/community/schools" },
          { label: "Nonprofits & Charities", href: "/community/nonprofits" },
          { label: "Responsible Businesses", href: "/community/businesses" },
        ],
      },
      {
        title: "Get Involved",
        items: [
          { label: "Volunteer Opportunities", href: "/community/opportunities" },
          { label: "Community Projects",      href: "/community/projects" },
          { label: "Events",                  href: "/community/events" },
          { label: "Discussion Groups",       href: "/community/groups" },
          { label: "Requests for Help",       href: "/community/requests" },
          { label: "Offers to Help",          href: "/community/offers" },
        ],
      },
    ],
  },
  {
    key: "passport", label: "My Passport", href: "/passport/me",
    groups: [
      {
        title: "Your Record",
        items: [
          { label: "Overview",              href: "/passport/me" },
          { label: "My Social Capital Score", href: "/my/score" },
          { label: "Category Breakdown",    href: "/my/categories" },
          { label: "Activity Timeline",     href: "/my/timeline" },
          { label: "Verification Status",   href: "/my/verification" },
          { label: "Badges & Levels",       href: "/my/badges" },
          { label: "Certificates",          href: "/my/certificates" },
          { label: "Skills & Experience",   href: "/my/skills" },
          { label: "Volunteer Record",      href: "/my/volunteer" },
        ],
      },
      {
        title: "Your Story",
        items: [
          { label: "Community Testimonials", href: "/my/testimonials" },
          { label: "My Causes",              href: "/my/causes" },
          { label: "My Projects",            href: "/my/projects" },
          { label: "My Public Portfolio",    href: "/my/portfolio" },
          { label: "My Legacy",              href: "/my/legacy" },
          { label: "Download or Share Passport", href: "/my/download" },
          { label: "QR Code",                href: "/my/qr" },
          { label: "Privacy & Visibility",   href: "/my/privacy" },
          { label: "Appeals & Corrections",  href: "/my/appeals" },
        ],
      },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Dropdown panel (desktop)                                           */
/* ------------------------------------------------------------------ */
function DropdownPanel({ item, search, onLink }) {
  if (item.quick && item.quick.length) {
    return (
      <div className="grid gap-1 min-w-[280px]" data-testid={`dd-${item.key}`}>
        {item.quick.map((q) => (
          <Link
            key={q.href}
            to={withBrandSearch(q.href, search)}
            onClick={onLink}
            className="rounded-lg px-3 py-2 text-sm hover:bg-emerald-50 transition-colors"
            style={{ color: "#0f172a" }}
            data-testid={`dd-item-${item.key}-${q.href}`}
          >
            {q.label}
          </Link>
        ))}
      </div>
    );
  }
  const cols = item.groups?.length || 1;
  return (
    <div
      className={`grid gap-6 ${cols > 1 ? "grid-cols-2" : "grid-cols-1"} min-w-[${cols > 1 ? "560" : "280"}px]`}
      style={{ minWidth: cols > 1 ? 560 : 280 }}
      data-testid={`dd-${item.key}`}
    >
      {item.groups.map((g, gi) => (
        <div key={gi}>
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2"
               style={{ color: "#059669" }}>
            {g.title}
          </div>
          <div className="grid gap-0.5">
            {g.items.map((it) => (
              <Link
                key={it.href}
                to={withBrandSearch(it.href, search)}
                onClick={onLink}
                className={`rounded-lg px-3 py-1.5 text-sm hover:bg-emerald-50 transition-colors ${it.highlight ? "font-semibold" : ""}`}
                style={{ color: it.highlight ? "#059669" : "#0f172a" }}
                data-testid={`dd-item-${item.key}-${it.href.replace(/[^a-z0-9]/gi, "-")}`}
              >
                {it.label}
              </Link>
            ))}
          </div>
        </div>
      ))}
      {item.cta && (
        <div className="col-span-full pt-4 border-t" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
          <Link
            to={withBrandSearch(item.cta.href, search)}
            onClick={onLink}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white"
            style={{ background: "#059669" }}
            data-testid={`dd-cta-${item.key}`}
          >
            <Sparkles className="w-3.5 h-3.5" /> {item.cta.label}
          </Link>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main nav                                                          */
/* ------------------------------------------------------------------ */
export default function SocialCapitalNav() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const [openKey, setOpenKey] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const containerRef = useRef(null);
  const closeTimer = useRef(null);

  // Close on route change.
  useEffect(() => { setOpenKey(null); setMobileOpen(false); setAccountOpen(false); }, [pathname, search]);

  // Close on outside click / ESC.
  useEffect(() => {
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpenKey(null); setAccountOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") { setOpenKey(null); setAccountOpen(false); } };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const openMenu = (k) => { clearTimeout(closeTimer.current); setOpenKey(k); };
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    // Small delay so cursor can cross the small gap between tab and panel.
    closeTimer.current = setTimeout(() => setOpenKey(null), 180);
  };

  const initial = (user?.name || user?.handle || "?").trim().charAt(0).toUpperCase();

  return (
    <div
      ref={containerRef}
      className="w-full border-b relative"
      style={{ background: "#FFFFFF", borderColor: "rgba(0,0,0,0.08)" }}
      data-testid="sc-nav"
    >
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 flex items-center gap-4 h-16">
        {/* Brand */}
        <Link
          to={withBrandSearch("/", search)}
          className="flex items-center gap-2 no-underline shrink-0"
          style={{ color: "#065F46" }}
          data-testid="sc-nav-brand"
        >
          <span
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
            style={{ background: "linear-gradient(135deg,#059669,#10b981)" }}
          >SC</span>
          <span className="font-display font-bold text-lg leading-none">
            <span className="hidden sm:inline">SocialCapital.cc</span>
            <span className="inline sm:hidden">SC.cc</span>
          </span>
        </Link>

        {/* Primary tabs (desktop) */}
        <nav className="hidden lg:flex items-center gap-1 flex-1" data-testid="sc-nav-tabs">
          {NAV_TREE.map((t) => {
            const isOpen = openKey === t.key;
            return (
              <div
                key={t.key}
                className="relative"
                onMouseEnter={() => openMenu(t.key)}
                onMouseLeave={scheduleClose}
              >
                <button
                  onClick={() => (isOpen ? setOpenKey(null) : setOpenKey(t.key))}
                  className={`inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${isOpen ? "bg-emerald-50" : "hover:bg-emerald-50"}`}
                  style={{ color: "#0f172a" }}
                  aria-expanded={isOpen}
                  data-testid={`sc-nav-tab-${t.key}`}
                >
                  {t.label}
                  <ChevronDown className={`w-3.5 h-3.5 opacity-60 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div
                    className="absolute left-0 top-full mt-1 rounded-2xl border bg-white p-4 shadow-lg z-40"
                    style={{ borderColor: "rgba(0,0,0,0.08)" }}
                    onMouseEnter={() => openMenu(t.key)}
                    onMouseLeave={scheduleClose}
                  >
                    <DropdownPanel item={t} search={search} onLink={() => setOpenKey(null)} />
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Utility (right side) */}
        <div className="ml-auto flex items-center gap-1 sm:gap-2">
          <button
            onClick={() => navigate(withBrandSearch("/search", search))}
            className="hidden sm:inline-flex w-9 h-9 rounded-full items-center justify-center hover:bg-emerald-50 transition-colors"
            aria-label="Search"
            data-testid="sc-nav-search"
          >
            <Search className="w-4 h-4" style={{ color: "#065F46" }} />
          </button>
          <button
            className="hidden sm:inline-flex w-9 h-9 rounded-full items-center justify-center hover:bg-emerald-50 transition-colors"
            aria-label="Language"
            data-testid="sc-nav-language"
            onClick={() => {
              const bar = document.querySelector('[data-testid="page-translate-trigger"]');
              if (bar) bar.click();
            }}
          >
            <Globe2 className="w-4 h-4" style={{ color: "#065F46" }} />
          </button>
          {user && (
            <button
              onClick={() => navigate(withBrandSearch("/notifications", search))}
              className="hidden sm:inline-flex w-9 h-9 rounded-full items-center justify-center hover:bg-emerald-50 transition-colors"
              aria-label="Notifications"
              data-testid="sc-nav-notifications"
            >
              <Bell className="w-4 h-4" style={{ color: "#065F46" }} />
            </button>
          )}

          {user ? (
            <div className="relative">
              <button
                onClick={() => setAccountOpen((v) => !v)}
                className="inline-flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-emerald-50 transition-colors"
                aria-expanded={accountOpen}
                data-testid="sc-nav-avatar"
              >
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <span
                    className="w-7 h-7 rounded-full inline-flex items-center justify-center text-white text-xs font-semibold"
                    style={{ background: "linear-gradient(135deg,#059669,#10b981)" }}
                  >{initial}</span>
                )}
                <ChevronDown className="w-3.5 h-3.5 opacity-60" />
              </button>
              {accountOpen && (
                <div
                  className="absolute right-0 top-full mt-2 rounded-2xl border bg-white p-2 shadow-lg z-40 min-w-[220px]"
                  style={{ borderColor: "rgba(0,0,0,0.08)" }}
                  data-testid="sc-nav-account-menu"
                >
                  <Link to={withBrandSearch("/passport/me", search)}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-emerald-50"
                        onClick={() => setAccountOpen(false)}>
                    <UserCircle className="w-4 h-4" /> My Passport
                  </Link>
                  <Link to={withBrandSearch("/my/score", search)}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-emerald-50"
                        onClick={() => setAccountOpen(false)}>
                    <LayoutDashboard className="w-4 h-4" /> My Dashboard
                  </Link>
                  <Link to={withBrandSearch("/my/privacy", search)}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-emerald-50"
                        onClick={() => setAccountOpen(false)}>
                    Privacy & Visibility
                  </Link>
                  <div className="border-t my-1" style={{ borderColor: "rgba(0,0,0,0.06)" }} />
                  <Link to={withBrandSearch("/logout", search)}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-emerald-50"
                        onClick={() => setAccountOpen(false)}>
                    Sign out
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <Link
              to={withBrandSearch("/login", search)}
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors"
              style={{ borderColor: "rgba(0,0,0,0.15)", color: "#0f172a" }}
              data-testid="sc-nav-signin"
            >
              <LogIn className="w-3.5 h-3.5" /> Sign In
            </Link>
          )}

          <Link
            to={withBrandSearch("/earn/submit", search)}
            className="inline-flex items-center gap-1.5 rounded-full px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-semibold text-white whitespace-nowrap"
            style={{ background: "#059669" }}
            data-testid="sc-nav-start"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Start Contributing</span>
            <span className="inline sm:hidden">Contribute</span>
          </Link>

          {/* Mobile menu button */}
          <button
            className="lg:hidden inline-flex w-9 h-9 rounded-full items-center justify-center hover:bg-emerald-50"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
            data-testid="sc-nav-mobile-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden border-t bg-white" style={{ borderColor: "rgba(0,0,0,0.06)" }}
             data-testid="sc-nav-mobile">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-3 space-y-3 max-h-[75vh] overflow-y-auto">
            {NAV_TREE.map((t) => (
              <details key={t.key} className="group">
                <summary
                  className="flex items-center justify-between py-2 cursor-pointer list-none"
                  data-testid={`sc-nav-mobile-tab-${t.key}`}
                >
                  <span className="text-sm font-semibold" style={{ color: "#0f172a" }}>{t.label}</span>
                  <ChevronDown className="w-4 h-4 opacity-60 group-open:rotate-180 transition-transform" />
                </summary>
                <div className="pl-2 pb-2">
                  <DropdownPanel item={t} search={search} onLink={() => setMobileOpen(false)} />
                </div>
              </details>
            ))}
            {!user && (
              <Link to={withBrandSearch("/login", search)}
                    className="block text-center rounded-full py-2 text-sm font-medium border"
                    style={{ borderColor: "rgba(0,0,0,0.15)" }}
                    onClick={() => setMobileOpen(false)}>
                Sign In
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
